import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { applyTheme, readStoredTheme, storeTheme, type ThemePreference } from '@/lib/theme';

interface UiState {
  /**
   * The *desktop* rail: wide (`w-64`) or icon-only (`w-16`). Applied through
   * `md:` variants only, because below that breakpoint the rail is not in the
   * page flow at all.
   */
  sidebarOpen: boolean;
  /**
   * The *phone/tablet* drawer, which overlays the page rather than displacing
   * it. Separate from `sidebarOpen` so each has the right default — a desktop
   * wants the rail open on arrival, a phone very much does not — without the
   * layout having to measure the viewport in JS.
   */
  mobileNavOpen: boolean;
  /**
   * The *preference*, which may be `'system'`. What is actually painted is
   * `resolveTheme(theme)` — read it through `useResolvedTheme()` rather than
   * comparing this to `'dark'`.
   */
  theme: ThemePreference;
  activeBranchId: string | null;
}

const initialState: UiState = {
  sidebarOpen: true,
  mobileNavOpen: false,
  theme: readStoredTheme(),
  activeBranchId: localStorage.getItem('venue.branch'),
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSidebar(state) {
      state.sidebarOpen = !state.sidebarOpen;
    },
    toggleMobileNav(state) {
      state.mobileNavOpen = !state.mobileNavOpen;
    },
    // Dispatched when a nav link is followed: a drawer that stays up over the
    // page it just navigated to is the classic off-canvas bug.
    closeMobileNav(state) {
      state.mobileNavOpen = false;
    },
    setTheme(state, action: PayloadAction<ThemePreference>) {
      state.theme = action.payload;
      storeTheme(action.payload);
      // Applied here as well as in `App`'s effect so the class lands in the same
      // frame as the click; the effect is what keeps `'system'` following the OS.
      applyTheme(action.payload);
    },
    setActiveBranch(state, action: PayloadAction<string | null>) {
      state.activeBranchId = action.payload;
      if (action.payload) localStorage.setItem('venue.branch', action.payload);
      else localStorage.removeItem('venue.branch');
    },
  },
});

export const { toggleSidebar, toggleMobileNav, closeMobileNav, setTheme, setActiveBranch } =
  uiSlice.actions;
export default uiSlice.reducer;
